/**
 * LocalizedDateInput - Date input with locale-aware format display
 * Displays and accepts dates in user's preferred format (yyyy-mm-dd, dd/mm/yyyy, mm/dd/yyyy)
 * Outputs ISO format (yyyy-mm-dd) for backend/form storage
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { useUserPreferences } from '../context';

export interface LocalizedDateInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Fires when the field starts or stops showing its own validation error.
   *
   * A rejected date keeps the text the user typed but is deliberately NOT sent up
   * through `onChange`, so `value` still holds the last accepted date. A form that
   * can submit needs this to notice the divergence — otherwise the user reads
   * "07/15/2026" in a red field and the submit sends the older date underneath it.
   */
  onValidityChange?: (isValid: boolean) => void;
  minDate?: string;
  maxDate?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Parse user input based on format preference
function parseUserDate(input: string, format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string | null {
  if (!input || input.trim() === '') return null;

  const cleaned = input.trim();
  let year: number, month: number, day: number;

  if (format === 'yyyy-mm-dd') {
    const match = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    [, year, month, day] = match.map(Number) as [unknown, number, number, number];
  } else if (format === 'dd/mm/yyyy') {
    const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    [, day, month, year] = match.map(Number) as [unknown, number, number, number];
  } else {
    // mm/dd/yyyy
    const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    [, month, day, year] = match.map(Number) as [unknown, number, number, number];
  }

  // Validate date components
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  // Validate actual date (handles month lengths, leap years)
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  // Return ISO format
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Format ISO date for user display
function formatForDisplay(isoDate: string | null, format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string {
  if (!isoDate) return '';

  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';

  const [, year, month, day] = match;

  if (format === 'yyyy-mm-dd') {
    return `${year}-${month}-${day}`;
  } else if (format === 'dd/mm/yyyy') {
    return `${day}/${month}/${year}`;
  } else {
    return `${month}/${day}/${year}`;
  }
}

// Get placeholder based on format
function getPlaceholder(format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string {
  return format.toUpperCase();
}

export function LocalizedDateInput({
  value,
  onChange,
  onValidityChange,
  minDate,
  maxDate,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  onBlur: externalOnBlur,
}: LocalizedDateInputProps) {
  const { t } = useTranslation('common');
  const { formatOptions } = useUserPreferences();
  const dateFormat = formatOptions.dateFormat;
  const generatedId = React.useId();

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value, dateFormat));
  const [validationError, setValidationError] = useState<string | null>(null);

  // Native date picker ref for calendar button
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Resync the display only when `value` or the format changes from OUTSIDE.
  //
  // The previous implementation derived this from `displayValue` and reran on every
  // keystroke, which made a rejected entry vanish: a min/max violation deliberately
  // withholds `onChange`, so `value` stayed put, the effect saw the mismatch, and it
  // wiped the field the user had just typed — with no message. Comparing against the
  // last synced pair instead means the user's own typing never triggers a resync,
  // while a parent-driven reset (dialog close, form reset) still does.
  const syncedRef = useRef({ value, dateFormat });
  useEffect(() => {
    if (syncedRef.current.value === value && syncedRef.current.dateFormat === dateFormat) return;
    syncedRef.current = { value, dateFormat };
    setDisplayValue(formatForDisplay(value, dateFormat));
    setValidationError(null);
  }, [value, dateFormat]);

  // Read through a ref so a caller passing an inline arrow doesn't have to memoize
  // it to avoid an extra notification on every render.
  const validityListenerRef = useRef(onValidityChange);
  validityListenerRef.current = onValidityChange;
  useEffect(() => {
    validityListenerRef.current?.(validationError === null);
  }, [validationError]);

  // Build input mask pattern based on format
  const inputPattern = useMemo(() => {
    if (dateFormat === 'yyyy-mm-dd') {
      return /^[\d-]*$/;
    }
    return /^[\d/]*$/;
  }, [dateFormat]);

  // Message for a date the user typed correctly but that falls outside the allowed
  // range — returns null when the date is acceptable.
  const boundsMessage = useCallback((iso: string): string | null => {
    if (minDate && iso < minDate) {
      return t('validation.dateNotBefore', { date: formatForDisplay(minDate, dateFormat) });
    }
    if (maxDate && iso > maxDate) {
      return t('validation.dateNotAfter', { date: formatForDisplay(maxDate, dateFormat) });
    }
    return null;
  }, [minDate, maxDate, dateFormat, t]);

  const formatMessage = useCallback(
    () => t('validation.dateFormat', { format: getPlaceholder(dateFormat) }),
    [t, dateFormat]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow empty input
    if (input === '') {
      setDisplayValue('');
      setValidationError(null);
      onChange(null);
      return;
    }

    // Only allow valid characters
    if (!inputPattern.test(input)) return;

    // Limit length (10 chars for full date)
    if (input.length > 10) return;

    setDisplayValue(input);

    // Only judge a date once it is long enough to be complete — flagging a
    // half-typed date would put an error under the field on the first keystroke.
    if (input.length < 10) {
      setValidationError(null);
      return;
    }

    const parsed = parseUserDate(input, dateFormat);
    if (!parsed) {
      setValidationError(formatMessage());
      return;
    }

    // A rejected date keeps the text the user typed; only `onChange` is withheld,
    // so the parent never stores a value outside its own bounds.
    const outOfRange = boundsMessage(parsed);
    setValidationError(outOfRange);
    if (outOfRange) return;

    onChange(parsed);
  }, [inputPattern, dateFormat, onChange, boundsMessage, formatMessage]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (displayValue === '') {
      setValidationError(null);
      onChange(null);
    } else {
      const parsed = parseUserDate(displayValue, dateFormat);
      if (!parsed) {
        setValidationError(formatMessage());
      } else {
        const outOfRange = boundsMessage(parsed);
        setValidationError(outOfRange);
        if (!outOfRange) {
          setDisplayValue(formatForDisplay(parsed, dateFormat));
          onChange(parsed);
        }
      }
    }

    externalOnBlur?.(e);
  }, [displayValue, dateFormat, onChange, externalOnBlur, boundsMessage, formatMessage]);

  // Handle native date picker selection
  const handleNativeDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isoValue = e.target.value;
    if (isoValue) {
      setDisplayValue(formatForDisplay(isoValue, dateFormat));
      setValidationError(null);
      onChange(isoValue);
    }
  }, [dateFormat, onChange]);

  const handleCalendarClick = useCallback(() => {
    hiddenInputRef.current?.showPicker();
  }, []);

  const inputId = id || `date-input-${generatedId}`;
  const messageId = `${inputId}-message`;
  const isError = validationError !== null || error === true;
  // The validation message takes the helper slot — one line under the field says
  // either what the field expects or what is wrong with it, never both.
  const message = validationError ?? helperText;

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label htmlFor={inputId} className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <div className="relative">
        <Input
          id={inputId}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder || getPlaceholder(dateFormat)}
          disabled={disabled}
          inputMode="numeric"
          aria-invalid={isError || undefined}
          aria-describedby={message ? messageId : undefined}
          className={cn(
            'pe-10',
            size === 'sm' && 'h-8 text-sm',
            isError && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute end-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={handleCalendarClick}
          disabled={disabled}
          aria-label={t('app.openCalendar')}
        >
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
      {message && (
        <p
          id={messageId}
          className={cn(
            'text-xs mt-1',
            isError ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      )}
      {/* Hidden native date picker for calendar functionality */}
      <input
        ref={hiddenInputRef}
        type="date"
        value={value || ''}
        min={minDate}
        max={maxDate}
        onChange={handleNativeDateChange}
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}
